import BubbleChartViz from '../vizs/BubbleChartViz'
import BaseComponent from './BaseComponent'
/**
 * {-EN-}
 * # {componentLabel} (`class {componentClassName}`)
 * Used to display three or four dimensions of data. Each entity of associated data is plotted as a bubble that expresses two of the values through the bubble’s xy location and the third through its size.
 *
 * Bubble charts can be considered a variation of the scatter plot, in which the data points are replaced with bubbles.
 *
 * ### Example Usage
 * ```javascript
 *   import {BubbleChart} from 'sdv-lib'
 *
 *   // Instantiate the chart with a DOM element, a data Array and a config Object
 *   let myChart = new BubbleChart($el, data, config);
 *
 * ```
 *
 * This chart can be considered as an extension of a [ScatterPlot](#/developer/ScatterPlotComponent), with an additional variable mapped into the bubble size
 *
 * ### Data structure {.section-data-structure}
 * The tabular data for this chart should be a table with 5 columns. Each row represents a bubble, and the first column should be the id/label, then its X and Y postion, its group (for colors) and its size.
 *
 * | ID       |     X  |    Y  |  Group   |   Size    |
 * | -------- | ------ |-------|----------|-----------|
 * |   a      |    22.4|   22.4|  Group 1 |   2.30    |
 * |   b      |    52.3|   12.5|  Group 2 |   2.45    |
 * |   c      |    12.8|   52.2|  Group 1 |   4.10    |
 * |   d      |    13.3|   12.6|  Group 2 |   5.12    |
 *
 * {.data-table}
 *
 * {-ES-}
 * # {componentLabelEs} (`class {componentClassName}`)
 * Se utiliza para mostrar tres o cuatro dimensiones de datos. Cada entidad de datos asociados se representa como una burbuja que expresa dos de los valores a través de la ubicación xy de la burbuja y el tercero a través de su tamaño.
 *
 * Los gráficos de burbujas pueden considerarse una variación del diagrama de dispersión, en el que los puntos de datos se reemplazan por burbujas.
 *
 * ### Ejemplo de uso
 * ```javascript
 *   import {BubbleChart} from 'sdv-lib'
 *
 *   // Instantiate the chart with a DOM element, a data Array and a config Object
 *   let myChart = new BubbleChart($el, data, config);
 *
 * ```
 *
 * Este gráfico puede considerarse como una extensión de un [Gráfico de dispersión](#/developer/ScatterPlotComponent), con una variable adicional asignada al tamaño de la burbuja.
 *
 * ### Estructura de datos {.section-data-structure}
 * Los datos tabulares de este gráfico deben ser una tabla con 5 columnas. Cada fila representa una burbuja, y la primera columna debe ser la identificación/etiqueta, luego su posición X e Y, su grupo (para colores) y su tamaño.
 *
 * | ID       |     X  |    Y  |  Grupo   |   Tamaño    |
 * | -------- | ------ |-------|----------|-----------|
 * |   a      |    22.4|   22.4|  Group 1 |   2.30    |
 * |   b      |    52.3|   12.5|  Group 2 |   2.45    |
 * |   c      |    12.8|   52.2|  Group 1 |   4.10    |
 * |   d      |    13.3|   12.6|  Group 2 |   5.12    |
 *
 * {.data-table}
 */
export default class BubbleChartComponent extends BaseComponent {
  static componentLabel = 'Bubble chart'
  static componentLabelEs = 'Gráfico de burbujas'
  static vizClass = BubbleChartViz
  static dataFormat = [
    { index: 0, axis: '', label: 'id', types: [String], role: 'id', roleLabel: 'Item' },
    {
      index: 1,
      axis: 'x0',
      label: 'x value',
      types: [Number, Date],
      role: 'value',
      roleLabel: 'X value - Number or Date (dd-mm-yyyy)',
    },
    {
      index: 2,
      axis: 'y0',
      label: 'x value',
      types: [Number, Date],
      role: 'value',
      roleLabel: 'Y value - Number or Date (dd-mm-yyyy)',
    },
    {
      index: 3,
      axis: '',
      label: 'size',
      types: [Number, null],
      role: 'value',
      roleLabel: 'Size value',
    },
    {
      index: 4,
      axis: '',
      label: 'color',
      types: [String, null],
      role: 'value',
      roleLabel: 'Category/Color',
      optional: true,
    },
  ]
  static defaultConfig = {
    title: this.componentLabel,
    TooltipBehavior: {
      axis: {
        x: {
          tooltipValueFormat: {
            exposed: true,
            nonEditable: false,
          },
        },
        y: {
          tooltipValueFormat: {
            exposed: true,
            nonEditable: false,
          },
        },
        z: {
          tooltipValueFormat: {
            exposed: true,
            nonEditable: false,
          },
        },
      },
    },
    hasColorPatterns: {
      default: true,
    },
  }
  static dataRequirements = {
    avoidDates: true,
  }
}
